import React from "react";
import {
  Snackbar,
  Alert,
  AlertTitle,
  Box,
  Slide,
  IconButton,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  CheckCircleRounded,
  ErrorRounded,
  WarningRounded,
  InfoRounded,
  CloseRounded,
} from "@mui/icons-material";

const FloatingAlert = ({ open, onClose, message, severity = "success" }) => {
  const theme = useTheme();

  const isDark = theme.palette.mode === "dark";

  const config = {
    success: {
      color: theme.palette.success?.main || "#10b981",
      title: "Berhasil",
      icon: <CheckCircleRounded sx={{ fontSize: 20 }} />,
    },
    error: {
      color: theme.palette.error?.main || "#ef4444",
      title: "Gagal",
      icon: <ErrorRounded sx={{ fontSize: 20 }} />,
    },
    warning: {
      color: theme.palette.warning?.main || "#f59e0b",
      title: "Perhatian",
      icon: <WarningRounded sx={{ fontSize: 20 }} />,
    },
    info: {
      color: theme.palette.info?.main || "#3b82f6",
      title: "Informasi",
      icon: <InfoRounded sx={{ fontSize: 20 }} />,
    },
  };

  const current = config[severity] || config.info;

  const Transition = (props) => <Slide direction="left" {...props} />;

  return (
    <Snackbar
      open={open}
      autoHideDuration={4500}
      onClose={onClose}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      TransitionComponent={Transition}
      sx={{
        top: { xs: 16, sm: 24 },
        right: { xs: 16, sm: 24 },
        zIndex: 9999,
        maxWidth: { xs: "calc(100vw - 32px)", sm: 400 },
        width: "100%",
      }}
    >
      <Alert
        onClose={onClose}
        severity={severity}
        variant="standard"
        icon={false}
        action={
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            onClick={onClose}
            sx={{
              p: 0.5,
              color: "text.secondary",
              opacity: 0.7,
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                opacity: 1,
                backgroundColor: isDark
                  ? "rgba(255, 255, 255, 0.08)"
                  : "rgba(0, 0, 0, 0.05)",
                transform: "scale(1.05)",
              },
            }}
          >
            <CloseRounded sx={{ fontSize: 18 }} />
          </IconButton>
        }
        sx={{
          width: "100%",
          position: "relative",
          overflow: "hidden",
          display: "flex",
          alignItems: "flex-start",
          p: "12px 14px 12px 16px",
          borderRadius: "12px",
          border: `1px solid ${
            isDark
              ? alpha(theme.palette.common.white, 0.1)
              : alpha(theme.palette.common.black, 0.08)
          }`,
          boxShadow: isDark
            ? "0 10px 30px rgba(0, 0, 0, 0.45)"
            : "0 10px 30px rgba(17, 31, 55, 0.12)",
          backgroundColor: isDark
            ? theme.palette.background.alt || "#1e1e1e"
            : theme.palette.background.alt || "#ffffff",
          color: "text.primary",
          backdropFilter: "blur(10px)",
          "&::before": {
            content: '""',
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: 4,
            backgroundColor: current.color,
          },
          "& .MuiAlert-message": {
            p: 0,
            width: "100%",
            display: "flex",
            alignItems: "flex-start",
            gap: 1.5,
          },
          "& .MuiAlert-action": {
            p: 0,
            m: 0,
            pl: 1,
            alignSelf: "flex-start",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 32,
            height: 32,
            flexShrink: 0,
            borderRadius: "50%",
            color: current.color,
            backgroundColor: alpha(current.color, 0.12),
          }}
        >
          {current.icon}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, pt: 0.25 }}>
          <AlertTitle
            sx={{
              m: 0,
              fontSize: "14px",
              fontWeight: 700,
              lineHeight: 1.2,
              letterSpacing: "-0.01em",
              color: "text.primary",
            }}
          >
            {current.title}
          </AlertTitle>
          {message && (
            <Box
              component="div"
              sx={{
                mt: 0.5,
                fontSize: "13px",
                lineHeight: 1.45,
                color: "text.secondary",
                wordBreak: "break-word",
              }}
            >
              {message}
            </Box>
          )}
        </Box>
      </Alert>
    </Snackbar>
  );
};

export default FloatingAlert;
